// 推廣活動落地頁（Godfather 套路，通用化版）——/godfather（默認首個上架活動）或 /godfather/:key
// 漏斗 events：campaign_view → campaign_form_start → campaign_submit(status)
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Gift, CheckCircle2, Loader2, Truck, MessageCircle, X, ListOrdered, Clock, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView, trackEvent, flushEvents } from '../lib/analytics';
import { apiFetchJson } from '../lib/api';
import { isMobile, buildWhatsAppUrl } from '../lib/whatsappDeeplink';
import SafeHtml from '../components/SafeHtml';
import { useCMS } from '../hooks/useCMS';

interface LeadCampaign {
  id: number;
  key: string;
  name: string;
  headline: string;
  subline: string;
  benefits: string[];
  detailsHtml?: string;
  gallery?: Array<{ url: string; caption?: string }>;
  deliveryMode?: string;
  pickupShops?: { name: string; address: string; merchantId?: number; quota?: number; claimedCount?: number }[];
  completedShares?: number;
  relayMode?: boolean;
}

// 無法取得活動配置時的後備文案（保持頁面永不白屏）
const FALLBACK: LeadCampaign = {
  id: 0, key: 'godfather-fb', name: 'FB 群友 Godfather 禮遇',
  headline: 'FB 群友專屬禮遇',
  subline: '免費辣粉試吃裝 直接郵寄到你屋企 ＋ 新客專屬折扣碼 即時發送',
  benefits: ['免費試吃裝郵寄到家（3-5 工作天）', '首單專屬折扣碼，即時發到 WhatsApp', '自動成為辣谷之友，朋友落單你有回贈'],
};

// 地區選擇（GoodStore2 同款）：3 大分區 optgroup＋細緻地區——ERP 客人畫像用
const HK_DISTRICT_GROUPS: { region: string; areas: string[] }[] = [
  {
    region: '香港島',
    areas: ['上環/中環/金鐘', '灣仔/銅鑼灣/跑馬地', '北角/鰂魚涌', '太古/西灣河/筲箕灣', '柴灣/小西灣', '香港仔/鴨脷洲', '薄扶林/數碼港', '山頂/半山區'],
  },
  {
    region: '九龍',
    areas: ['尖沙咀/佐敦', '油麻地', '旺角/大角咀', '紅磡/何文田', '九龍城/土瓜灣', '啟德/九龍灣', '黃大仙/新蒲崗', '彩虹/慈雲山', '觀塘/藍田', '深水埗/長沙灣', '石硤尾/九龍塘'],
  },
  {
    region: '新界',
    areas: ['荃灣', '葵涌/葵芳', '青衣', '屯門', '元朗', '天水圍', '大埔', '沙田/大圍', '馬鞍山', '上水/粉嶺', '將軍澳', '西貢', '東涌', '離島'],
  },
];

// 谷友體驗流程（landing 流程彈窗用）：6 步直向 Stepper，首次接觸都睇得明
const FLOW_STEPS_MAIL = [
  { icon: '🎁', title: '按「WhatsApp 立即領取」', desc: '揀中禮品，填姓名同郵寄地址（30 秒搞掂）' },
  { icon: '✅', title: 'WhatsApp 按發送', desc: '自動彈出驗證訊息，按一下「發送」證明係你（防亂領）' },
  { icon: '📲', title: '確認即時到', desc: '登記確認＋任務詳情即時發到你嘅 WhatsApp' },
  { icon: '📦', title: '禮品寄到你門口', desc: '3-5 個工作天，免運費直送' },
  { icon: '📸', title: '出分享帖', desc: '收到禮品影相＋真實感想，tag #香港辣谷' },
  { icon: '🔗', title: '貼連結完成任務', desc: '將分享帖連結傳返俾我哋，谷友福利繼續有你份' },
];
const FLOW_STEPS_PICKUP = [
  { icon: '🎁', title: '按「WhatsApp 立即領取」', desc: '揀禮品＋揀門店，留個 WhatsApp 號碼（20 秒搞掂）' },
  { icon: '✅', title: 'WhatsApp 按發送', desc: '自動彈出驗證訊息，按一下「發送」證明係你（防亂領）' },
  { icon: '📲', title: '確認即時到', desc: '登記確認＋專屬 QR 即時發到你嘅 WhatsApp' },
  { icon: '🏪', title: '到門店出示 QR', desc: '10 日內到揀咗嘅門店，出示 QR 免費領取' },
  { icon: '📸', title: '出分享帖', desc: '收到禮品影相＋真實感想，tag #香港辣谷' },
  { icon: '🔗', title: '貼連結完成任務', desc: '將分享帖連結傳返俾我哋，谷友福利繼續有你份' },
];

export default function Godfather() {
  const navigate = useNavigate();
  const { key: keyParam } = useParams();
  const { getValue } = useCMS();
  const businessPhone = getValue('site.whatsapp.number', '85260771493') || '85260771493';
  const [params] = useSearchParams();
  const [campaign, setCampaign] = useState<LeadCampaign>(FALLBACK);
  const [campaignLoaded, setCampaignLoaded] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [region, setRegion] = useState('');
  const [consent, setConsent] = useState(true);
  const [taskCommit, setTaskCommit] = useState(false);
  const [taskPlatforms, setTaskPlatforms] = useState<string[]>(['FB']);
  const [phoneInput, setPhoneInput] = useState('');
  const [pickupShop, setPickupShop] = useState('');
  const [shopOffers, setShopOffers] = useState<Array<{ key: string; title: string; imageUrl: string; validUntil: string }>>([]);
  const isPickupMode = String(campaign.deliveryMode || '') === 'pickup';
  const isPickup = isPickupMode; // 並行 session 指名的別名
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ promo: string; referralCode: string } | null>(null);
  const [verify, setVerify] = useState<{ code: string; text: string } | null>(null);
  const [verifyDone, setVerifyDone] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState<string>('claimed');
  const [phoneChoice, setPhoneChoice] = useState<{ code: string; verifiedAs: string; formPhone: string } | null>(null);
  const [phoneUpdated, setPhoneUpdated] = useState<string | null>(null);
  const [confirmingPhone, setConfirmingPhone] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [galIdx, setGalIdx] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const formStartedRef = useRef(false);
  const viewTrackedRef = useRef(false);

  useEffect(() => {
    trackPageView('/godfather');
    if (params.get('utm_source') || params.get('r')) {
      trackEvent('session_entry', { landing: '/godfather', campaign: 'fb_godfather' });
      flushEvents();
    }
    // 載入活動配置（失敗用後備文案，仍可提交——後端兼容無 campaignKey）
    apiFetchJson<LeadCampaign>(keyParam ? `/public/lead-campaigns/${encodeURIComponent(keyParam)}` : '/public/lead-campaigns')
      .then((res) => {
        if (res.success && res.data) setCampaign({ ...FALLBACK, ...res.data });
        setCampaignLoaded(true);
      })
      .catch(() => setCampaignLoaded(true));
  }, [keyParam]);

  // 漏斗：view（配置載入後記一次，帶實際 campaignKey）
  useEffect(() => {
    if (campaignLoaded && !viewTrackedRef.current) {
      viewTrackedRef.current = true;
      trackEvent('campaign_view', { campaignKey: campaign.key });
      flushEvents();
    }
  }, [campaignLoaded]);

  // 漏斗：form_start（首次輸入）
  const markFormStart = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackEvent('campaign_form_start', { campaignKey: campaign.key });
  };

  // X1493 下單同款：window.open 優先（新手頁），被攔截即轉同頁導航——永不卡死
  const openWhatsAppLink = (link: string) => {
    const win = window.open(link, '_blank');
    if (!win || win.closed) window.location.href = link;
  };
  // 跳轉後輪詢驗證狀態：webhook 核實 → 頁面翻牌（成功／已登記過／網絡限制／碼過期）
  useEffect(() => {
    if (!verify || verifyDone) return undefined;
    const t = window.setInterval(() => {
      fetch(`/api/public/godfather/verify-status/${verify.code}`)
        .then((r) => r.json())
        .then((d) => {
          const dd = d.data || {};
          if (dd.verified) {
            setVerifyStatus(dd.status || 'claimed'); setVerifyDone(true);
            if (dd.formPhone && dd.verifiedAs) setPhoneChoice({ code: verify.code, verifiedAs: dd.verifiedAs, formPhone: dd.formPhone });
          }
          else if (dd.status === 'dup_phone' || dd.status === 'dup_ip' || dd.status === 'invalid') {
            setVerifyStatus(dd.status); setVerifyDone(true);
          }
        })
        .catch(() => {});
    }, 3000);
    return () => window.clearInterval(t);
  }, [verify, verifyDone]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // 店級名額防護：揀中滿額店（剩餘 ≤0）唔准提交
    if (isPickupMode && pickupShop) {
      const shop = (campaign.pickupShops || []).find((x) => x.name === pickupShop);
      const remaining = shop?.quota ? shop.quota - (shop.claimedCount || 0) : null;
      if (remaining !== null && remaining <= 0) {
        setError('呢間門店名額已滿，請揀返其他門店');
        return;
      }
    }
    setSubmitting(true);
    try {
      const pDigits = phoneInput.replace(/\D/g, '');
      const res = await fetch('/api/public/godfather/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({ name, address: isPickupMode ? `到店領取：${pickupShop}` : address, region, campaignKey: campaign.key, marketingConsent: consent, taskCommitment: taskCommit, taskPlatforms, phone: pDigits.length === 8 ? `852${pDigits}` : pDigits, pickupShop: pickupShop || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error || data.detail || '提交失敗，請稍後再試';
        const status = res.status === 409 ? 'dup_phone' : res.status === 429 ? 'dup_ip' : /結束|不存在/.test(msg) ? 'campaign_ended' : 'error';
        trackEvent('campaign_submit', { campaignKey: campaign.key, status });
        flushEvents();
        throw new Error(msg);
      }
      trackEvent('campaign_submit', { campaignKey: campaign.key, status: 'ok' });
      trackEvent('godfather_join', { source: 'fb_group', campaignKey: campaign.key });
      flushEvents();
      const vd = (data.data || data) as { verifyCode?: string; verifyText?: string };
      if (!vd.verifyCode) throw new Error('驗證碼生成失敗，請重試');
      const vText = vd.verifyText || `香港辣谷禮品驗證 ${vd.verifyCode}`;
      trackEvent('campaign_verify_pending', { campaignKey: campaign.key });
      flushEvents();
      setVerify({ code: vd.verifyCode, text: vText });
      // 一按即跳：submit 成功後同一瞬間打開 WhatsApp（手機 wa.me／桌面 WhatsApp Web）
      openWhatsAppLink(buildWhatsAppUrl(businessPhone, vText, !isMobile()));
    } catch (e: any) {
      setError(e.message || '提交失敗，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none bg-white text-stone-800";

  // 雙 WhatsApp：popup 揀選用邊個號碼入系統
  const confirmPhone = async (useFormPhone: boolean) => {
    if (!phoneChoice || confirmingPhone) return;
    if (!useFormPhone) { setPhoneChoice(null); return; }
    setConfirmingPhone(true);
    try {
      const res = await fetch('/api/public/godfather/confirm-phone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({ code: phoneChoice.code, phone: phoneChoice.formPhone }),
      });
      if (res.ok) setPhoneUpdated(phoneChoice.formPhone);
      setPhoneChoice(null);
    } finally {
      setConfirmingPhone(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <section className="py-10 md:py-16">
        <div className="max-w-lg mx-auto px-4">
          {verifyDone ? (
            verifyStatus === 'dup_phone' || verifyStatus === 'dup_ip' || verifyStatus === 'invalid' ? (
              <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  {verifyStatus === 'invalid'
                    ? <Clock size={32} className="text-amber-600" />
                    : <AlertCircle size={32} className="text-amber-600" />}
                </div>
                <h1 className="text-2xl font-bold mb-2 text-stone-900">
                  {verifyStatus === 'invalid' ? '⏰ 驗證碼已過期' : verifyStatus === 'dup_phone' ? '✋ 呢個號碼已登記過' : '每個網絡地址限領一次'}
                </h1>
                <p className="text-stone-600 text-sm mb-4">
                  {verifyStatus === 'invalid'
                    ? '唔緊要！返去重新提交一次表格就得（提交後 30 分鐘內按發送有效）'
                    : verifyStatus === 'dup_phone'
                      ? '確認訊息之前已發到你嘅 WhatsApp——如有疑問搵客服 🌶️'
                      : '多謝支持！家人朋友想領，請用佢哋自己嘅網絡登記——有疑問搵客服 🌶️'}
                </p>
                {verifyStatus === 'invalid' && (
                  <button
                    onClick={() => { setVerify(null); setVerifyDone(false); setVerifyStatus('claimed'); }}
                    className="bg-x1493-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-red-700 transition">
                    重新填寫
                  </button>
                )}
                <p className="text-xs text-stone-400 mt-4">可以關閉此頁——我哋 WhatsApp 見 🌶️</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-green-200 p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={32} className="text-green-600" />
                </div>
                <h1 className="text-2xl font-bold mb-2 text-stone-900">✅ 驗證成功！</h1>
                <p className="text-stone-600 text-sm mb-1">{verifyStatus === 'waitlist' ? '你已列入後補名單——確認已發到你嘅 WhatsApp；一有名額即刻通知你 📲' : '登記確認＋任務詳情已經發到你嘅 WhatsApp 📲'}</p>
                {phoneUpdated && <p className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-1.5 inline-block mb-1">✅ 聯絡號碼已按你選擇更新為 {phoneUpdated}</p>}
                <p className="text-xs text-stone-400">可以關閉此頁——我哋 WhatsApp 見 🌶️</p>
              </div>
            )
          ) : verify ? (
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift size={32} className="text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2 text-stone-900">幾乎完成！最後一步驗證</h1>
              <p className="text-stone-600 text-sm mb-5">我哋已為你準備好驗證訊息——<br />跳去 WhatsApp 按一下「發送」，禮品即時確認 ✅</p>
              <div className="bg-stone-100 rounded-xl px-4 py-3 font-mono font-bold text-lg text-stone-800 mb-4 tracking-widest">{verify.code}</div>
              <a href={buildWhatsAppUrl(businessPhone, verify.text, !isMobile())}
                onClick={(e) => { trackEvent('campaign_verify_open', { campaignKey: campaign.key }); flushEvents(); if (!isMobile()) { e.preventDefault(); openWhatsAppLink(buildWhatsAppUrl(businessPhone, verify.text, true)); } }}
                className="block w-full bg-[#25D366] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#128C7E] transition flex items-center justify-center gap-2">
                <MessageCircle size={20} /> 打開 WhatsApp 發送驗證
              </a>
              <p className="text-xs text-stone-400 mt-4">驗證碼 30 分鐘內有效 · 發送後，確認訊息會即時發到你嘅 WhatsApp</p>
              <button onClick={() => setVerify(null)} className="mt-3 text-stone-400 text-xs underline">重新填寫</button>
            </div>
          ) : !done ? (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift size={32} className="text-x1493-primary" />
                </div>
                <h1 className="text-3xl font-bold mb-2 text-stone-900">{campaign.headline || campaign.name}</h1>
                <p className="text-stone-600">{campaign.subline}</p>
                <button
                  onClick={() => { setFlowOpen(true); trackEvent('campaign_flow_open', { campaignKey: campaign.key }); flushEvents(); }}
                  className="mt-4 mx-auto flex items-center gap-2 px-6 py-3 rounded-full bg-red-50 text-red-700 border-2 border-red-300 hover:bg-red-100 hover:border-red-400 transition text-base font-bold shadow-sm">
                  <ListOrdered size={18} /> 📋 玩法一目了然（10 秒讀完）
                </button>
              </div>

              {campaign.benefits.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6 space-y-2.5">
                  {[
                    { icon: <Truck size={18} />, text: campaign.benefits[0] },
                    { icon: <Gift size={18} />, text: campaign.benefits[1] },
                    { icon: <MessageCircle size={18} />, text: campaign.benefits[2] },
                  ].filter((b) => b.text).map((b, i) => (
                    <div key={i} className="flex items-center gap-3 text-stone-700 text-sm">
                      <span className="text-x1493-primary">{b.icon}</span>
                      {b.text}
                    </div>
                  ))}
                </div>
              )}

              {(campaign.detailsHtml || '').trim() && (
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
                  <p className="text-sm font-bold text-stone-900 dark:text-white mb-2">🌶️ 任務超簡單：收到禮品 → 影相＋一句感想 → tag #香港辣谷</p>
                  {(campaign.completedShares || 0) > 0 && (
                    <p className="text-xs text-x1493-primary font-bold mb-2">🔥 已有 {campaign.completedShares} 位谷友完成分享——跟住佢哋一齊玩</p>
                  )}
                  <details className="text-left">
                    <summary className="text-xs text-stone-400 cursor-pointer select-none hover:text-stone-600 dark:hover:text-gray-300">睇完整任務詳情（會連同登記確認一齊 WhatsApp 發俾你）</summary>
                    <SafeHtml html={campaign.detailsHtml} className="mt-2 text-xs text-stone-600 leading-relaxed [&_h3]:font-bold [&_h3]:text-stone-800 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-2 [&_blockquote]:border-x1493-primary/40 [&_blockquote]:pl-3 [&_blockquote]:text-stone-500" />
                  </details>
                </div>
              )}

              {campaign.gallery && campaign.gallery.length > 0 && (() => {
                const gal = campaign.gallery!;
                const gi = Math.min(galIdx, gal.length - 1);
                const go = (d: number) => setGalIdx((cur) => (Math.min(cur, gal.length - 1) + d + gal.length) % gal.length);
                return (
                  <div className="mb-6">
                    <div
                      className="relative"
                      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                      onTouchEnd={(e) => {
                        if (touchStartX.current === null) return;
                        const dx = e.changedTouches[0].clientX - touchStartX.current;
                        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
                        touchStartX.current = null;
                      }}
                    >
                      <div className="aspect-[3/4] overflow-hidden rounded-2xl border border-stone-200 bg-stone-100 cursor-pointer" onClick={() => setLightboxIdx(gi)}>
                        <img key={gi} src={gal[gi].url} alt={gal[gi].caption || `活動相片 ${gi + 1}`} className="w-full h-full object-cover select-none" draggable={false} />
                      </div>
                      {gal.length > 1 && (
                        <>
                          <button type="button" aria-label="上一張" onClick={(e) => { e.stopPropagation(); go(-1); }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center text-stone-700">
                            <ChevronLeft size={20} />
                          </button>
                          <button type="button" aria-label="下一張" onClick={(e) => { e.stopPropagation(); go(1); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center text-stone-700">
                            <ChevronRight size={20} />
                          </button>
                          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-xs">{gi + 1}/{gal.length}</span>
                        </>
                      )}
                    </div>
                    {gal[gi].caption && <p className="text-xs text-stone-500 mt-1.5 text-center">{gal[gi].caption}</p>}
                    {gal.length > 1 && (
                      <div className="flex items-center justify-center gap-1.5 mt-2">
                        {gal.map((_, i) => (
                          <button key={i} type="button" aria-label={`相片 ${i + 1}`} onClick={() => setGalIdx(i)}
                            className={`w-2 h-2 rounded-full transition ${i === gi ? 'bg-x1493-primary' : 'bg-stone-300'}`} />
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-stone-400 text-center mt-1.5">點擊相片可放大</p>
                  </div>
                );
              })()}

              <form onSubmit={submit} onFocus={markFormStart} className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 space-y-4">
                {!isPickupMode && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">姓名 *</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="收件人姓名" className={inputCls} required />
                </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">WhatsApp 號碼 *<span className="text-xs text-stone-400 ml-1">（方便我哋聯絡確認）</span></label>
                  <input value={phoneInput} onChange={(e) => setPhoneInput(e.target.value.replace(/\D/g, ''))} maxLength={15} minLength={8}
                    inputMode="tel" pattern="\d{8,15}" placeholder="9XXXXXXX" className={inputCls} required />
                </div>

                {!isPickupMode && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">地區 *<span className="text-xs text-stone-400 ml-1">（方便安排郵寄同地區福利）</span></label>
                  <select value={region} onChange={(e) => setRegion(e.target.value)} className={inputCls} required>
                    <option value="" disabled>請選擇地區</option>
                    {HK_DISTRICT_GROUPS.map((g) => (
                      <optgroup key={g.region} label={g.region}>
                        {g.areas.map((a) => <option key={a} value={`${g.region}·${a}`}>{a}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
                )}
                {isPickup ? (
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">揀領取門店 *<span className="text-xs text-stone-400 ml-1">（到店出示 QR 領取）</span></label>
                    <select value={pickupShop} onChange={async (e) => {
                      const name = e.target.value;
                      setPickupShop(name);
                      setShopOffers([]);
                      const shop = (campaign.pickupShops || []).find((x) => x.name === name);
                      if (shop?.merchantId) {
                        try {
                          const r = await fetch(`/api/public/godfather/merchant-offers/${shop.merchantId}`);
                          const d = await r.json().catch(() => ({}));
                          setShopOffers(((d.data || {}).offers) || []);
                        } catch { /* 優惠載入失敗唔阻登記 */ }
                      }
                    }} className={inputCls} required>
                      <option value="">請選擇門店</option>
                      {(campaign.pickupShops || []).map((x) => {
                        const remaining = x.quota ? x.quota - (x.claimedCount || 0) : null;
                        const full = remaining !== null && remaining <= 0;
                        return (
                          <option key={x.name} value={x.name} disabled={full}>
                            {x.name}{x.address ? `（${x.address}）` : ''}{remaining !== null ? ` · 剩餘 ${Math.max(0, remaining)}` : ''}{full ? '（已滿）' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {pickupShop && shopOffers.length > 0 && (
                      <div className="mt-2 bg-orange-50 border border-orange-200 rounded-xl p-3">
                        <p className="text-xs font-bold text-orange-800 mb-2">🎁 順便試埋呢間店嘅招牌優惠（到店出示即可）：</p>
                        <div className="space-y-2">
                          {shopOffers.map((o) => (
                            <div key={o.key} className="flex items-center gap-2.5 bg-white rounded-lg p-2 border border-orange-100">
                              {o.imageUrl && <img src={o.imageUrl} alt={o.title} className="w-10 h-10 object-cover rounded-lg flex-shrink-0" />}
                              <div className="min-w-0">
                                <p className="text-xs font-bold text-stone-800 truncate">{o.title}</p>
                                {o.validUntil && <p className="text-[10px] text-stone-500">至 {o.validUntil}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-orange-700 mt-1.5">領取禮品後向店員出示即可享有 🌶️</p>
                      </div>
                    )}
                  </div>
                ) : (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">完整郵寄地址 *<span className="text-xs text-stone-400 ml-1">（大廈／村屋請寫詳細，不接受❌順豐柜）</span></label>
                  <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={3} maxLength={200}
                    placeholder="例：九龍旺角彌敦道 123 號幸福大廈 12 樓 A 室" className={inputCls} required />
                </div>
                )}
                {error && <div className="bg-red-50 text-red-700 p-3 rounded-xl text-sm">{error}</div>}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 space-y-2.5">
                  <label className="flex items-start gap-2 text-xs text-stone-700 cursor-pointer">
                    <input type="checkbox" checked={taskCommit} onChange={(e) => setTaskCommit(e.target.checked)} required className="mt-0.5 w-4 h-4 accent-red-600 flex-shrink-0" />
                    <span>我承諾收到禮品後 <b>7 日內</b> 出分享帖（影相＋真實感想＋tag <b>#香港辣谷</b>）✅</span>
                  </label>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-stone-500">打算喺邊度分享？</span>
                    {['FB', 'IG', '小紅書', 'Threads'].map((pf) => (
                      <button type="button" key={pf}
                        onClick={() => setTaskPlatforms((prev) => prev.includes(pf) ? prev.filter((x) => x !== pf) : [...prev, pf])}
                        className={`text-xs px-2.5 py-1 rounded-full border transition ${taskPlatforms.includes(pf) ? 'bg-x1493-primary text-white border-x1493-primary' : 'bg-white border-stone-300 text-stone-600 hover:border-x1493-primary'}`}>
                        {pf}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-start gap-2 text-xs text-stone-500 cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-red-600 flex-shrink-0" />
                  <span>同意香港辣谷透過 WhatsApp 發送優惠及會員通訊（可隨時回覆拒收）</span>
                </label>
                <button type="submit" disabled={submitting}
                  className="w-full bg-[#25D366] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#128C7E] transition disabled:opacity-60 flex items-center justify-center gap-2">
                  {submitting ? <Loader2 size={20} className="animate-spin" /> : <MessageCircle size={20} />}
                  {submitting ? '領取中…' : 'WhatsApp 立即領取'}
                </button>
                <p className="text-xs text-stone-400 text-center">每個號碼／網絡限領一次 · 資料只用於本次禮遇郵寄</p>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={48} className="text-green-600" />
              </div>
              <h1 className="text-3xl font-bold mb-3">領取成功！🎉</h1>
              <p className="text-stone-600 mb-6">{name}，你嘅禮遇已啟動！</p>
              <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4 text-left mb-6">
                <div className="border-b border-stone-100 pb-4">
                  <p className="text-sm text-stone-500 mb-1">📋 你的專屬折扣碼</p>
                  <p className="text-2xl font-mono font-bold text-x1493-primary">{done.promo || '已發送到 WhatsApp'}</p>
                  <p className="text-xs text-stone-400 mt-1">結帳時輸入即享折扣</p>
                </div>
                <div>
                  <p className="text-sm text-stone-500 mb-1">📮 試吃裝將寄出至</p>
                  <p className="font-medium text-stone-800">{address}</p>
                  <p className="text-xs text-stone-400 mt-1">3-5 個工作天內出貨</p>
                </div>
                <div>
                  <p className="text-sm text-stone-500 mb-1">📲 完整確認訊息</p>
                  <p className="text-sm text-stone-700">已發送到你的 WhatsApp，記得留意訊息！</p>
                </div>
              </div>
              <button onClick={() => navigate('/store')}
                className="w-full bg-x1493-primary text-white py-4 rounded-xl font-bold text-lg hover:bg-red-700 transition flex items-center justify-center gap-2">
                <Gift size={20} /> 用折扣碼即刻去行街
              </button>
              <button onClick={() => navigate('/')} className="mt-3 text-stone-500 hover:text-stone-700 text-sm underline">返回首頁</button>
            </div>
          )}
        </div>
      </section>
      {lightboxIdx !== null && campaign.gallery && campaign.gallery[lightboxIdx] && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxIdx(null)} role="dialog" aria-modal="true">
          <button className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="關閉" onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}>
            <X size={24} />
          </button>
          <img src={campaign.gallery[lightboxIdx].url} alt={campaign.gallery[lightboxIdx].caption || ''} className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          {campaign.gallery[lightboxIdx].caption && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/90 text-sm">{campaign.gallery[lightboxIdx].caption}</p>
          )}
        </div>
      )}

      {/* 谷友體驗・流程彈窗（手機＝底部 Sheet／桌面＝置中卡；直向 Stepper） */}
      {flowOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setFlowOpen(false)} role="dialog" aria-modal="true" aria-label="谷友體驗流程">
          <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl max-h-[88vh] overflow-y-auto p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-stone-900">谷友體驗・玩法一目了然</h2>
              <button onClick={() => setFlowOpen(false)} aria-label="關閉" className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-500">
                <X size={18} />
              </button>
            </div>
            <ol className="relative ml-4 border-l-2 border-stone-200 space-y-6">
              {(isPickupMode ? FLOW_STEPS_PICKUP : FLOW_STEPS_MAIL).map((st, i) => (
                <li key={i} className="relative pl-8">
                  <span className="absolute -left-[15px] top-0 w-7 h-7 rounded-full bg-x1493-primary text-white text-xs font-bold flex items-center justify-center shadow">{i + 1}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{st.icon}</span>
                    <span className="font-bold text-stone-900">{st.title}</span>
                  </div>
                  <p className="text-xs text-stone-500 mt-1 leading-relaxed">{st.desc}</p>
                </li>
              ))}
            </ol>
            <button onClick={() => setFlowOpen(false)} className="mt-7 w-full bg-x1493-primary text-white py-3.5 rounded-xl font-bold hover:bg-red-700 transition">
              明白！即刻領取 🌶️
            </button>
          </div>
        </div>
      )}

      {/* 雙 WhatsApp 確認 popup：偵測到表格號碼 ≠ 驗證發送號碼 */}
      {verifyDone && phoneChoice && verifyStatus !== 'dup_phone' && verifyStatus !== 'dup_ip' && verifyStatus !== 'invalid' && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setPhoneChoice(null)} role="dialog" aria-modal="true" aria-label="確認 WhatsApp 號碼">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl">📱</div>
            <h3 className="text-lg font-bold text-stone-900 mb-1">確認你嘅 WhatsApp 號碼</h3>
            <p className="text-xs text-stone-500 mb-4">偵測到你用唔同號碼發送驗證——揀一個我哋用嚟聯絡你：</p>
            <button onClick={() => confirmPhone(true)} disabled={confirmingPhone}
              className="w-full mb-2 bg-[#25D366] text-white py-3 rounded-xl font-bold hover:bg-[#128C7E] transition disabled:opacity-60">
              用 {phoneChoice.formPhone}（表格填寫）
            </button>
            <button onClick={() => confirmPhone(false)} disabled={confirmingPhone}
              className="w-full bg-stone-100 text-stone-700 py-3 rounded-xl font-medium hover:bg-stone-200 transition">
              用 {phoneChoice.verifiedAs}（啱先發送驗證）
            </button>
            <p className="text-[11px] text-stone-400 mt-3">揀錯都唔緊——之後隨時回覆號碼更改</p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
